import React from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaBoxes, FaWarehouse, FaBox, FaChartBar, FaChartLine, FaBell, FaCrosshairs } from 'react-icons/fa';
import SEO from '../../components/common/SEO';

const benefitIcons = {
  1: <FaWarehouse />,
  2: <FaBox />,
  3: <FaChartBar />,
  4: <FaChartLine />,
  5: <FaBell />,
  6: <FaCrosshairs />
};

const StockManagementPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();

  return (
    <>
      <SEO 
        titleKey="features.stockManagement.seo.title"
        descriptionKey="features.stockManagement.seo.description"
      />
      <PageContainer>
        {/* Hero Section */}
        <HeroSection>
          <HeroContent>
            <HeroTag>{t('features.stockManagement.hero.tag')}</HeroTag>
            <HeroTitle>{t('features.stockManagement.hero.title')}</HeroTitle>
            <HeroSubtitle>{t('features.stockManagement.hero.subtitle')}</HeroSubtitle>
            <CTAButtons>
              <PrimaryButton to={`/${lang}/demo`}>
                {t('features.stockManagement.hero.ctaPrimary')}
              </PrimaryButton>
              <SecondaryButton to={`/${lang}/contact`}>
                {t('features.stockManagement.hero.ctaSecondary')}
              </SecondaryButton>
            </CTAButtons>
          </HeroContent>
          <HeroImageContainer>
            <HeroIcon><FaBoxes /></HeroIcon>
          </HeroImageContainer>
        </HeroSection>

        {/* Features Grid */}
        <FeaturesSection>
          <SectionTitle>{t('features.stockManagement.benefits.title')}</SectionTitle>
          <FeaturesGrid>
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <FeatureCard key={num}>
                <FeatureIcon>{benefitIcons[num]}</FeatureIcon>
                <FeatureTitle>{t(`features.stockManagement.benefits.items.benefit${num}.title`)}</FeatureTitle>
                <FeatureDescription>
                  {t(`features.stockManagement.benefits.items.benefit${num}.description`)}
                </FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </FeaturesSection>

        {/* How It Works */}
        <HowItWorksSection>
          <SectionTitle>{t('features.stockManagement.howItWorks.title')}</SectionTitle>
          <StepsContainer>
            {[1, 2, 3, 4].map((num) => (
              <StepCard key={num}>
                <StepNumber>{num}</StepNumber>
                <StepContent>
                  <StepTitle>{t(`features.stockManagement.howItWorks.steps.step${num}.title`)}</StepTitle>
                  <StepDescription>
                    {t(`features.stockManagement.howItWorks.steps.step${num}.description`)}
                  </StepDescription>
                </StepContent>
              </StepCard>
            ))}
          </StepsContainer>
        </HowItWorksSection>

        {/* CTA Section */}
        <CTASection>
          <CTAContent>
            <CTATitle>{t('features.stockManagement.cta.title')}</CTATitle>
            <CTADescription>{t('features.stockManagement.cta.description')}</CTADescription>
            <CTAButton to={`/${lang}/demo`}>
              {t('features.stockManagement.cta.button')}
            </CTAButton>
          </CTAContent>
        </CTASection>
      </PageContainer>
    </>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  padding: 120px 5% 80px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    padding: 100px 5% 60px;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  color: white;
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.95;
  margin-bottom: 2rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled(Link)`
  background: white;
  color: #f5576c;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: white;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  text-decoration: none;
  border: 2px solid white;
  transition: all 0.3s ease;

  &:hover {
    background: white;
    color: #f5576c;
  }
`;

const HeroImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 968px) {
    margin-top: 2rem;
  }
`;

const HeroIcon = styled.div`
  font-size: 15rem;
  filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3));
  animation: float 3s ease-in-out infinite;

  @keyframes float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  @media (max-width: 768px) {
    font-size: 8rem;
  }
`;

const FeaturesSection = styled.section`
  padding: 80px 5%;
  background: #f8f9fa;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4rem;
  color: #1a202c;

  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 2rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const FeatureCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  text-align: center;
  color: ${props => props.theme?.colors?.primary || '#dc2626'};

  svg {
    width: 3rem;
    height: 3rem;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1a202c;
`;

const FeatureDescription = styled.p`
  color: #4a5568;
  line-height: 1.6;
`;

const HowItWorksSection = styled.section`
  padding: 80px 5%;
  background: white;
`;

const StepsContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const StepCard = styled.div`
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  padding: 2rem;
  background: #f8f9fa;
  border-radius: 20px;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    color: white;

    h3, p {
      color: white;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    align-items: center;
  }
`;

const StepNumber = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #f5576c;
  min-width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

  ${StepCard}:hover & {
    color: white;
    background: rgba(255, 255, 255, 0.2);
  }
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #1a202c;
`;

const StepDescription = styled.p`
  color: #4a5568;
  line-height: 1.6;
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  padding: 80px 5%;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  color: white;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;
  opacity: 0.95;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: white;
  color: #f5576c;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.125rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
  }
`;

export default StockManagementPage;

